import {
    getCategories,
    getCategoryPrices,
    getCategoryOffers,
    createStream,
    getStream,
    getPurchases,
    getPurchase,
    purchasePackage,
    getStreams,
    updateStream,
    getMonthlyLeadStatus,
    cancelMonthlyLeadSubscription,
    changeMonthlyLeadAllocation,
} from './agent';

import { parseStreamList } from './streamList';

import type {
    LeadCategory,
    LeadPackagePrice,
    LeadPricingOffer,
    CreateStreamRequest,
    Stream,
    UpdateStreamRequest,
    PurchaseRequest,
    PurchaseResponse,
    LeadPackagePurchase,
    MonthlyLeadStatus,
} from './agent';

class LeadStreamsService {
    queryKey = 'lead-streams';

    getPurchase = async (id: number): Promise<LeadPackagePurchase> => (await getPurchase(id)).data;

    getMonthlyLeadStatus = async (streamId: number): Promise<MonthlyLeadStatus> => {
        try {
            return (await getMonthlyLeadStatus(streamId)).data;
        } catch (error: any) {
            if (error?.response?.status === 404) return { available: false, subscription: null };
            throw error;
        }
    };

    cancelMonthlyLeadSubscription = async (streamId: number): Promise<MonthlyLeadStatus> =>
        (await cancelMonthlyLeadSubscription(streamId)).data;

    changeMonthlyLeadAllocation = async (streamId: number, packageSize: number): Promise<MonthlyLeadStatus> =>
        (await changeMonthlyLeadAllocation(streamId, packageSize)).data;

    getCategories = async (): Promise<LeadCategory[]> => {
        try {
            const { data } = await getCategories();
            // Обрабатываем случай, когда API возвращает объект с results (пагинация)
            if (data && typeof data === 'object' && 'results' in data && Array.isArray((data as any).results)) {
                return Promise.resolve((data as any).results);
            }
            // Или просто массив
            if (Array.isArray(data)) {
                return Promise.resolve(data);
            }
            return Promise.resolve([]);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    getCategoryPrices = async (categoryId: number): Promise<LeadPackagePrice[]> => {
        try {
            const { data } = await getCategoryPrices(categoryId);
            return Promise.resolve(data);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    getCategoryOffers = async (categoryId: number): Promise<LeadPricingOffer[]> => {
        try {
            const { data } = await getCategoryOffers(categoryId);
            return Promise.resolve(data);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    getStreams = async (options?: { signal?: AbortSignal }): Promise<Stream[]> => {
        const { data } = await getStreams(options?.signal);
        return parseStreamList(data);
    };

    getPurchases = async (): Promise<LeadPackagePurchase[]> => {
        try {
            const { data } = await getPurchases();
            if (data && typeof data === 'object' && 'results' in data && Array.isArray((data as any).results)) {
                return Promise.resolve((data as any).results);
            }
            if (Array.isArray(data)) {
                return Promise.resolve(data);
            }
            return Promise.resolve([]);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    createStream = async (data: CreateStreamRequest): Promise<Stream> => {
        try {
            const { data: response } = await createStream(data);
            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    getStream = async (streamId: number, signal?: AbortSignal): Promise<Stream> => {
        try {
            const { data } = await getStream(streamId, signal);
            return Promise.resolve(data);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    updateStream = async (streamId: number, data: UpdateStreamRequest): Promise<Stream> => {
        try {
            const { data: response } = await updateStream(streamId, data);
            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };

    purchasePackage = async (streamId: number, data: PurchaseRequest): Promise<PurchaseResponse> => {
        try {
            const { data: response } = await purchasePackage(streamId, data);
            return Promise.resolve(response);
        } catch (error) {
            return Promise.reject(error);
        }
    };
}

export default new LeadStreamsService();
